import { compact, filter, find, flatMap, get, uniqBy } from 'lodash';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map as rxMap, take } from 'rxjs/operators';
import {
  GameType,
  MediaType,
  GameGroupType,
  LeaderboardType,
  GamePointType,
  GameAssetsType,
  GetGameWinnersGQL,
  TopLeaderboardsType,
  GameIntegrationType,
  LeaderboardCycleEnum,
  GameSettingsType,
  GameAssetDetailType,
  GameIntegrationCustomType,
  GameRewardWithGameType,
  GameSettingsItemType,
  GameTranslationType,
  GameIntegrationLeaderboardType,
  GetCurrentDayRewardsGQL,
  GetCurrentWeekRewardsGQL,
  CurrentRewardsFilterInput,
  GetGameIntegrationByTargetGQL,
  GameIntegrationSettingsType,
  GameRewardWithGameAndClosingDateType,
  GetLiveTopGamesLeaderboardByCycleGQL,
} from '@sifca-monorepo/widget-generator';
import { StorageHelper } from '@diktup/frontend/helpers';

interface GameWinnersInput {
  gameGroup: string;
  gameCode?: string;
  cycle?: LeaderboardCycleEnum;
}

export type WidgetResolvedGame = GameType & {
  custom?: GameIntegrationCustomType | null;
  leaderboard?: GameIntegrationLeaderboardType | null;
};
@Injectable({
  providedIn: 'root',
})
export class MobileGamesService {
  private wheelGameIntegration: BehaviorSubject<GameIntegrationSettingsType> = new BehaviorSubject(null);
  private loadingGames: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private gameGroup: BehaviorSubject<GameGroupType> = new BehaviorSubject(null);
  private scorzGame: BehaviorSubject<GameGroupType> = new BehaviorSubject(null);
  private games: BehaviorSubject<WidgetResolvedGame[]> = new BehaviorSubject(null);
  private gameWinners: BehaviorSubject<LeaderboardType[]> = new BehaviorSubject(null);
  private selectedGame: BehaviorSubject<WidgetResolvedGame> = new BehaviorSubject(null);
  private topLeaderboard: BehaviorSubject<GameRewardWithGameType[]> = new BehaviorSubject(null);
  private dayRewards: BehaviorSubject<GameRewardWithGameAndClosingDateType> = new BehaviorSubject(null);
  private weekRewards: BehaviorSubject<GameRewardWithGameAndClosingDateType> = new BehaviorSubject(null);

  pageIndex = 0;
  pageLimit = 10;
  partnerIntegration: GameIntegrationType;
  leaderCycle: LeaderboardCycleEnum = LeaderboardCycleEnum.DAILY;

  get wheelGameIntegration$(): Observable<GameIntegrationSettingsType> {
    return this.wheelGameIntegration.asObservable();
  }

  get scorzGame$(): Observable<GameGroupType> {
    return this.scorzGame.asObservable();
  }

  get gameWinners$(): Observable<LeaderboardType[]> {
    return this.gameWinners.asObservable();
  }
  set gameWinners$(value: LeaderboardType[]) {
    this.gameWinners.next(value);
  }

  get gameGroup$(): Observable<GameGroupType> {
    return this.gameGroup.asObservable();
  }
  set gameGroup$(value: any) {
    this.gameGroup.next(value);
  }

  get selectedGame$(): Observable<WidgetResolvedGame> {
    return this.selectedGame.asObservable();
  }
  set selectedGame$(value: WidgetResolvedGame) {
    this.selectedGame.next(value);
  }

  get selectedGameSnapshot(): WidgetResolvedGame | null {
    return this.selectedGame.getValue();
  }

  setSelectedGame(game: WidgetResolvedGame | null): void {
    this.selectedGame.next(game);
  }

  clearSelectedGame(): void {
    this.selectedGame.next(null);
  }

  get topLeaderboard$(): Observable<GameRewardWithGameType[]> {
    return this.topLeaderboard.asObservable();
  }
  set topLeaderboard$(value: GameRewardWithGameType[]) {
    this.topLeaderboard.next(value);
  }

  get dayRewards$(): Observable<GameRewardWithGameAndClosingDateType> {
    return this.dayRewards.asObservable();
  }
  set dayRewards$(value: GameRewardWithGameAndClosingDateType) {
    this.dayRewards.next(value);
  }

  get weekRewards$(): Observable<GameRewardWithGameAndClosingDateType> {
    return this.weekRewards.asObservable();
  }
  set weekRewards$(value: GameRewardWithGameAndClosingDateType) {
    this.weekRewards.next(value);
  }

  get loadingGames$(): Observable<boolean> {
    return this.loadingGames.asObservable();
  }

  get games$(): Observable<GameType[]> {
    return this.games.asObservable();
  }
  set games$(value: WidgetResolvedGame[] | null) {
    this.games.next(value);
  }

  constructor(
    private storageHelper: StorageHelper,
    private getGameWinnersGQL: GetGameWinnersGQL,
    private getCurrentDayRewardsGQL: GetCurrentDayRewardsGQL,
    private getCurrentWeekRewardsGQL: GetCurrentWeekRewardsGQL,
    private getGameIntegrationByTargetGQL: GetGameIntegrationByTargetGQL,
    private getLiveTopGamesLeaderboardByCycleGQL: GetLiveTopGamesLeaderboardByCycleGQL,
  ) {}

  getCurrentDayRewards(input: Partial<CurrentRewardsFilterInput> = {}): Observable<GameRewardWithGameAndClosingDateType | null> {
    return this.getCurrentDayRewardsGQL
      .fetch({
        input: {
          ...input,
          target: { pos: this.storageHelper.getData('posId') },
        },
      })
      .pipe(
        take(1),
        rxMap(({ data }: any) => {
          const currentDayRewards = data?.getCurrentDayRewards;
          this.dayRewards.next(currentDayRewards);
          return currentDayRewards;
        }),
        catchError(() => {
          this.dayRewards.next(null);
          return of(null);
        }),
      );
  }

  getCurrentWeekRewards(input: Partial<CurrentRewardsFilterInput> = {}): Observable<GameRewardWithGameAndClosingDateType | null> {
    const rewardsInput: CurrentRewardsFilterInput = {
      ...input,
      target: { pos: this.storageHelper.getData('posId') },
    } as CurrentRewardsFilterInput;

    return this.getCurrentWeekRewardsGQL.fetch({ input: rewardsInput }).pipe(
      take(1),
      rxMap(({ data }: any) => {
        const currentWeekRewards = data?.getCurrentWeekRewards || null;
        this.weekRewards.next(currentWeekRewards);
        return currentWeekRewards;
      }),
      catchError(() => {
        this.weekRewards.next(null);
        return of(null);
      }),
    );
  }

  getGameWinners(input: GameWinnersInput): Observable<LeaderboardType[]> {
    return this.getGameWinnersGQL
      .fetch({
        ...(input?.gameGroup ? { gameGroup: input.gameGroup } : {}),
        ...(input?.gameCode ? { gameCode: input.gameCode } : {}),
        ...(input?.cycle ? { cycle: input.cycle } : {}),
        target: { pos: this.storageHelper.getData('posId') },
      })
      .pipe(
        take(1),
        rxMap(({ data }: any) => {
          this.gameWinners.next(data.getGameWinners);
          return data.getGameWinners;
        }),
        catchError(() => {
          this.gameWinners.next([]);
          return of([]);
        }),
      );
  }

  getLiveTopGamesLeaderboardByCycle(): Observable<TopLeaderboardsType | null> {
    const posId = this.storageHelper.getData('posId');
    if (!posId) {
      this.topLeaderboard.next([]);
      return of(null);
    }

    return this.getLiveTopGamesLeaderboardByCycleGQL.fetch({ cycle: this.leaderCycle, target: { pos: posId } }).pipe(
      take(1),
      rxMap(({ data }: any) => {
        if (data) {
          const board = filter(data.getLiveTopGamesLeaderboardByCycle.leaderboards, (b: GameRewardWithGameType) => b.game.code != 'SCZ');
          this.topLeaderboard.next(board);
          return data.getLiveTopGamesLeaderboardByCycle;
        }
        this.topLeaderboard.next([]);
        return null;
      }),
      catchError((error) => {
        this.topLeaderboard.next([]);
        return of(null);
      }),
    );
  }

  getGameGroupGames(): Observable<WidgetResolvedGame[]> {
    this.loadingGames.next(true);
    return this.getGameIntegrationByTargetGQL
      .fetch({
        target: {
          pos: this.storageHelper.getData('posId'),
        },
      })
      .pipe(
        take(1),
        rxMap(({ data }: any) => {
          const gameIntegration = data?.getGameIntegrationByTarget as GameIntegrationType | undefined;
          const wheelGameIntegration = gameIntegration?.games?.find((game) => game.game.code === 'WHL');
          if (wheelGameIntegration) {
            const wheelGameIntegration = gameIntegration?.games?.find((game) => game.game.code === 'WHL');
            if (wheelGameIntegration) {
              const resolvedGame = this.applyLocalizedGameContent(
                this.resolveGameWithCustom(wheelGameIntegration.game, wheelGameIntegration.custom)
              );
            
              this.wheelGameIntegration.next({
                ...wheelGameIntegration,
                game: resolvedGame,
              } as any);
            }
          }
          const boxGames = filter(this.normalizeIntegrationGames(gameIntegration), (game) => game.code !== 'SCZ');
          const gameGroup = gameIntegration?.gameGroups?.[0];

          this.loadingGames.next(false);
          this.gameGroup.next(gameGroup ? { ...gameGroup, games: boxGames } : null);
          this.games.next(boxGames);
          return boxGames;
        }),
        catchError(() => {
          this.loadingGames.next(false);
          this.games.next([]);
          return of([]);
        }),
      );
  }

  normalizeIntegrationGames(integration?: GameIntegrationType): WidgetResolvedGame[] {
    if (!integration) {
      return [];
    }

    const integrationGames = compact(get(integration, 'games', [])) as GameIntegrationSettingsType[];
    const groupedGames = compact(flatMap(get(integration, 'gameGroups', []), 'games')) as GameType[];
    const candidateGames = groupedGames.length
      ? groupedGames
      : integrationGames.map((gameEntry: GameIntegrationSettingsType) => gameEntry?.game).filter(Boolean);

    return uniqBy(
      candidateGames.reduce((acc, game: GameType) => {
        const match = this.findIntegrationGameByIdentity(integrationGames, game);
        if (match?.enable === false) {
          return acc;
        }

        const resolvedGame = this.applyLocalizedGameContent(this.resolveGameWithCustom(game, match?.custom));
        acc.push({
          ...resolvedGame,
          custom: match?.custom ?? null,
          leaderboard: match?.leaderboard ?? null,
        });

        return acc;
      }, [] as WidgetResolvedGame[]),
      (game: WidgetResolvedGame) => this.getGameIdentity(game),
    );
  }

  private findIntegrationGameByIdentity(games: GameIntegrationSettingsType[], game: GameType): GameIntegrationSettingsType | undefined {
    return find(games, (entry: GameIntegrationSettingsType) => this.getGameIdentity(entry?.game ?? entry) === this.getGameIdentity(game));
  }

  private resolveGameWithCustom(game: GameType, custom?: GameIntegrationCustomType | null): GameType {
    if (!custom) {
      return game;
    }

    return {
      ...game,
      assets: this.mergeAssets(game.assets, custom.assets),
      description: custom.description ?? game.description,
      media: this.mergeMedia(game.media, custom.media),
      point: this.mergePoint(game.point, custom.point),
      settings: this.mergeSettings(game.settings, custom.settings),
      title: custom.title ?? game.title,
      translation: this.mergeTranslations(game.translation, custom.translation),
    };
  }

  private applyLocalizedGameContent(game: GameType): GameType {
    const translation = this.findPreferredTranslation(game.translation);
    if (!translation?.content) {
      return game;
    }

    return {
      ...game,
      title: translation.content.title ?? game.title,
      description: translation.content.description ?? game.description,
    };
  }

  private findPreferredTranslation(translations?: GameTranslationType[] | null): GameTranslationType | undefined {
    if (!translations?.length) {
      return undefined;
    }

    const languageTokens = this.getPreferredLanguageTokens();
    for (const token of languageTokens) {
      const match = translations.find((translation) => {
        const languageCode = String(translation?.language?.code ?? '').toLowerCase();
        const languageName = String(translation?.language?.name ?? '').toLowerCase();
        return languageCode === token || languageCode.startsWith(`${token}-`) || languageName.includes(token);
      });

      if (match) {
        return match;
      }
    }

    return (
      translations.find((translation) =>
        String(translation?.language?.code ?? '')
          .toLowerCase()
          .startsWith('en'),
      ) ?? translations[0]
    );
  }

  private getPreferredLanguageTokens(): string[] {
    const rawLanguage = String(this.storageHelper.getData('lang') || this.storageHelper.getData('elvklang') || 'en').toLowerCase();
    const normalizedLanguage = rawLanguage.replace('_', '-');
    const baseLanguage = normalizedLanguage.split('-')[0];
    const tokens = [normalizedLanguage, baseLanguage];

    if (baseLanguage === 'ar') {
      tokens.push('arabic');
    } else if (baseLanguage === 'fr') {
      tokens.push('french');
    } else if (baseLanguage === 'en') {
      tokens.push('english');
    }

    return Array.from(new Set(tokens.filter(Boolean)));
  }

  private mergeAssets(base?: GameAssetsType | null, custom?: GameAssetsType | null): GameAssetsType | null | undefined {
    if (!base && !custom) {
      return base ?? custom;
    }

    return {
      ...base,
      ...custom,
      font: custom?.font ?? base?.font,
      images: this.mergeAssetDetails(base?.images, custom?.images),
      sound: this.mergeAssetDetails(base?.sound, custom?.sound),
    };
  }

  private mergeAssetDetails(base?: GameAssetDetailType[] | null, custom?: GameAssetDetailType[] | null): GameAssetDetailType[] | null | undefined {
    if (!base && !custom) {
      return base ?? custom;
    }

    if (!base?.length) {
      return custom ?? [];
    }

    if (!custom?.length) {
      return base;
    }

    const customByReference = new Map(custom.map((detail, index) => [detail?.reference || `custom:${index}`, detail]));
    const merged: GameAssetDetailType[] = [];
    const usedKeys = new Set<string>();

    base.forEach((detail, index) => {
      const key = detail?.reference || `base:${index}`;
      merged.push(customByReference.get(key) ?? detail);
      usedKeys.add(key);
    });

    custom.forEach((detail, index) => {
      const key = detail?.reference || `custom:${index}`;
      if (!usedKeys.has(key)) {
        merged.push(detail);
      }
    });

    return merged;
  }

  private mergeMedia(base?: MediaType | null, custom?: MediaType | null): MediaType | null | undefined {
    if (!base && !custom) {
      return base ?? custom;
    }

    return {
      ...base,
      ...custom,
      deg360: custom?.deg360 ?? base?.deg360,
      pictures: custom?.pictures ?? base?.pictures,
      videos: custom?.videos ?? base?.videos,
    };
  }

  private mergePoint(base?: GamePointType | null, custom?: GamePointType | null): GamePointType {
    if (!custom) {
      return base as GamePointType;
    }

    return {
      ...base,
      ...custom,
      code: custom.code ?? base?.code ?? '',
      name: custom.name ?? base?.name ?? '',
      picture: custom.picture ?? base?.picture,
    } as GamePointType;
  }

  private mergeSettings(base?: GameSettingsType | null, custom?: GameSettingsType | null): GameSettingsType | null | undefined {
    if (!base && !custom) {
      return base ?? custom;
    }

    return {
      ...base,
      ...custom,
      color: custom?.color ?? base?.color,
      cost: custom?.cost ?? base?.cost,
      gameConfigs: custom?.gameConfigs ?? base?.gameConfigs,
      items: this.mergeSettingsItems(base?.items, custom?.items),
    };
  }

  private mergeSettingsItems(
    base?: GameSettingsItemType[] | null,
    custom?: GameSettingsItemType[] | null,
  ): GameSettingsItemType[] | null | undefined {
    if (!base && !custom) {
      return base ?? custom;
    }

    const maxLength = Math.max(base?.length ?? 0, custom?.length ?? 0);
    const items: GameSettingsItemType[] = [];

    for (let index = 0; index < maxLength; index++) {
      const baseItem = base?.[index];
      const customItem = custom?.[index];
      if (!baseItem && !customItem) {
        continue;
      }

      items.push({
        ...baseItem,
        ...customItem,
        again: customItem?.again ?? baseItem?.again ?? false,
        color: customItem?.color ?? baseItem?.color,
        description: customItem?.description ?? baseItem?.description ?? '',
        emoji: customItem?.emoji ?? baseItem?.emoji,
        empty: customItem?.empty ?? baseItem?.empty ?? false,
        fallback: customItem?.fallback ?? baseItem?.fallback,
        isRare: customItem?.isRare ?? baseItem?.isRare,
        picture: customItem?.picture ?? baseItem?.picture,
        points: customItem?.points ?? baseItem?.points,
        price: customItem?.price ?? baseItem?.price,
        qualitative: customItem?.qualitative ?? baseItem?.qualitative,
        title: customItem?.title ?? baseItem?.title ?? '',
        weight: customItem?.weight ?? baseItem?.weight,
      } as GameSettingsItemType);
    }

    return items;
  }

  private mergeTranslations(base?: GameTranslationType[] | null, custom?: GameTranslationType[] | null): GameTranslationType[] | null | undefined {
    if (!base && !custom) {
      return base ?? custom;
    }

    if (!base?.length) {
      return custom ?? [];
    }

    if (!custom?.length) {
      return base;
    }

    const customByLanguage = new Map(custom.map((translation) => [translation?.language?.id, translation]));
    const merged: GameTranslationType[] = [];
    const usedLanguageIds = new Set<string>();

    base.forEach((translation) => {
      const languageId = translation?.language?.id;
      if (!languageId) {
        merged.push(translation);
        return;
      }

      merged.push(customByLanguage.get(languageId) ?? translation);
      usedLanguageIds.add(languageId);
    });

    custom.forEach((translation) => {
      const languageId = translation?.language?.id;
      if (!languageId || !usedLanguageIds.has(languageId)) {
        merged.push(translation);
      }
    });

    return merged;
  }

  private getGameIdentity(game: any): string {
    const id = get(game, 'id');
    const code = get(game, 'code');
    return id ? `id:${id}` : code ? `code:${code}` : '';
  }
}
